import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Radio,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  InsertDriveFileOutlined as InsertDriveFileIcon,
  LinkOutlined as LinkIcon,
} from "@mui/icons-material";
import AttachmentThumbnail from "../../../tables/AttachmentThumbnail.jsx";

const isImage = (name = "", mimeType = "") =>
  /\.(png|jpe?g|gif|webp|svg)$/i.test(name) || mimeType.startsWith("image/");

const formatBytes = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Individual selectable file tile used inside FileGrid.
 *
 * Props:
 *   file              — { id, name, description?, size?, mimeType?, url?, previewUrl?, isSession, usedIn? }
 *                       where usedIn is an array of { source: "cost"|"form", label: string }
 *   selected          — boolean
 *   onToggle          — (id: string) => void
 *   multiple          — boolean  (renders Checkbox vs Radio indicator)
 *   uploading         — boolean  (shows a spinner overlay; reserved for future upload-progress use)
 *   addDescription    — boolean  allow editing file descriptions
 *   onUpdateDescription — (id: string, description: string) => void
 *   addUsedIn         — boolean  allow tagging source categorization (session files only)
 *   usedInSuggestions — string[]  autocomplete suggestions from claim's existing tags
 *   onUpdateUsedIn    — (uuid: string, tags: string[]) => void
 *   selectable        — boolean  (default true) - if false, card is disabled/read-only
 */
export default function FileCard({
  file,
  selected,
  onToggle,
  multiple,
  uploading = false,
  addDescription = false,
  onUpdateDescription,
  addUsedIn = false,
  usedInSuggestions = [],
  onUpdateUsedIn,
  showUsedInTags = false,
  selectable = true,
}) {
  const showImage = isImage(file.name, file.mimeType ?? "");
  // Session files carry a client-side blob: URL (from the just-picked File
  // object) and can be rendered directly. Existing (already-persisted)
  // files only expose a server path, which requires the authenticated
  // fetch AttachmentThumbnail performs — a plain <img src> would not carry
  // the Bearer auth token.
  const blobPreviewSrc = file.previewUrl ?? null;
  const serverPreviewUrl = file.serverPreviewUrl ?? null;

  // The selectable ID is the server ID (used in RHF)
  const selectId = file.id;

  const SelectionControl = multiple ? Checkbox : Radio;

  const usedInSources = Array.isArray(file.usedIn)
    ? file.usedIn
    : file.usedIn
      ? [file.usedIn]
      : [];

  // Merge persisted used_in strings with session-derived usedIn labels, deduplicated
  const mergedUsedInTags = [
    ...(file.used_in ?? []),
    ...usedInSources.map((u) => u.label),
  ].filter((tag, i, arr) => arr.indexOf(tag) === i);

  const tooltipTitle = selectable
    ? file.name
    : `${file.name}\nBereits verknüpft mit: ${usedInSources.map((u) => u.label).join(", ")}`;

  return (
    <Tooltip title={tooltipTitle} placement="top" enterDelay={600}>
      <Box
        component="li"
        role={multiple ? "checkbox" : "radio"}
        aria-checked={selected}
        aria-label={file.name}
        tabIndex={0}
        onClick={() => !uploading && selectable && onToggle(selectId)}
        onKeyDown={(e) => {
          if (
            (e.key === "Enter" || e.key === " ") &&
            !uploading &&
            selectable
          ) {
            e.preventDefault();
            onToggle(selectId);
          }
        }}
        sx={{
          listStyle: "none",
          position: "relative",
          width: 270,
          borderRadius: 2,
          border: "2px solid",
          borderColor: selected ? "primary.main" : "divider",
          cursor: uploading ? "wait" : selectable ? "pointer" : "not-allowed",
          p: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          bgcolor: selected
            ? "primary.50"
            : selectable
              ? "background.paper"
              : "action.disabledBackground",
          opacity: selectable ? 1 : 0.6,
          outline: "none",
          transition:
            "border-color 0.15s, background-color 0.15s, box-shadow 0.15s, opacity 0.15s",
          userSelect: "none",
          "&:hover": {
            borderColor: uploading || !selectable ? "divider" : "primary.light",
            boxShadow: uploading || !selectable ? 0 : 2,
          },
          "&:focus-visible": {
            boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}40`,
          },
        }}
      >
        {/* Selection indicator — top-right corner */}
        <Box sx={{ position: "absolute", top: 2, right: 2, lineHeight: 0 }}>
          <SelectionControl
            checked={selected}
            size="small"
            tabIndex={-1} // focus is on the card itself
            sx={{ p: 0.25 }}
            // Prevent the control's own click from double-firing with the card's
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(selectId)}
            inputProps={{ "aria-hidden": true, tabIndex: -1 }}
            disabled={!selectable}
          />
        </Box>

        {/* Thumbnail or file icon */}
        <Box
          sx={{
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "action.hover",
            flexShrink: 0,
          }}
        >
          {showImage && blobPreviewSrc ? (
            <Box
              component="img"
              src={blobPreviewSrc}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : serverPreviewUrl ? (
            <AttachmentThumbnail previewUrl={serverPreviewUrl} size={64} />
          ) : (
            <InsertDriveFileIcon
              sx={{ fontSize: 36, color: "text.disabled" }}
            />
          )}
        </Box>

        {/* File name */}
        <Typography
          variant="caption"
          sx={{
            width: "100%",
            // textAlign: "center",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2, // allows 2 lines
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: selected ? 600 : 400,
          }}
        >
          {file.name}
        </Typography>

        {/* File size */}
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: "text.disabled",
          }}
        >
          {formatBytes(file.size)}
        </Typography>

        {/* usedIn badges — merged from persisted tags and session cross-references */}
        {showUsedInTags
          ? mergedUsedInTags.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  width: "100%",
                  mt: 0.5,
                }}
              >
                {mergedUsedInTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ fontSize: "0.6rem", height: 18 }}
                  />
                ))}
              </Box>
            )
          : usedInSources.map((source, idx) => (
              <Chip
                key={idx}
                icon={<LinkIcon sx={{ fontSize: "0.75rem !important" }} />}
                label={source.label}
                size="small"
                color={source.color || "info"}
                variant="outlined"
                sx={{
                  fontSize: "0.65rem",
                  height: 20,
                  mt: 0.5,
                  "& .MuiChip-icon": { marginLeft: "4px", marginRight: "-2px" },
                }}
              />
            ))}

        {/* Description field for session files when addDescription is enabled */}
        {addDescription && file.isSession && (
          <TextField
            size="small"
            placeholder="Bezeichnung"
            value={file.description ?? ""}
            onChange={(e) => {
              if (!onUpdateDescription) {
                console.warn("FileCard: onUpdateDescription is undefined");
                return;
              }
              const fileKey = file.isSession ? file.uuid : file.id;
              onUpdateDescription(fileKey, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={!selectable}
            sx={{
              width: "100%",
              mt: 0.5,
              "& .MuiInputBase-root": {
                fontSize: "0.75rem",
                height: 28,
              },
              "& .MuiInputBase-input": {
                py: 0.5,
              },
            }}
          />
        )}

        {/* used_in tag input for session files when addUsedIn is enabled */}
        {addUsedIn && file.isSession && (
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={usedInSuggestions}
            value={file.used_in ?? []}
            onChange={(e, newValue) => {
              onUpdateUsedIn?.(file.uuid, newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ fontSize: "0.65rem", height: 18 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Kategorie…" size="small" />
            )}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={!selectable}
            sx={{
              width: "100%",
              mt: 0.5,
              "& .MuiInputBase-root": { fontSize: "0.75rem", minHeight: 28 },
            }}
          />
        )}

        {/* Upload progress overlay */}
        {uploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              bgcolor: "background.paper",
              opacity: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
